'use client'

import { Title } from '@mantine/core'
import { useEffect } from 'react'
import type { ProfileSectionData } from '../_actions/getProfileSectionData'

import { useImageWithCache } from '../_hooks/useImageWithCache'
import { BabyNameModal } from './BabyNameModal'
import { UploadImageModal } from './UploadImageModal'

export function ProfileSection({ profileSectionData, isEditable }: { profileSectionData: ProfileSectionData; isEditable: boolean }) {
	const whichProfileImageToShow = profileSectionData[0]?.profileImage || '/default/profile.jpg'
	const whichCoverImageToShow = profileSectionData[0]?.coverImage || '/default/cover.jpg'

	// Use custom hooks to manage cached images
	const [coverImageUrl, setCoverImageUrl] = useImageWithCache('cover', whichCoverImageToShow)
	const [profileImageUrl, setProfileImageUrl] = useImageWithCache('profile', whichProfileImageToShow)

	const profileId = profileSectionData[0]?.profileId?.toString() || null
	const babyName = profileSectionData[0]?.babyName
	const eventType = profileSectionData[0]?.eventType

	useEffect(() => {
		// Scroll to top when component mounts
		window.scrollTo(0, 0)

		// Alternative method
		document.documentElement.scrollTop = 0
	}, [])

	if (!profileId) {
		return null
	}

	return (
		<div className="relative w-full h-[280px] sm:h-[320px] lg:h-[350px]">
			{/* Cover photo with edit functionality */}
			<UploadImageModal id={profileId} setImageUrl={setCoverImageUrl} isEditable={isEditable} mode="cover">
				<div className={`relative w-full h-[280px] sm:h-[320px] lg:h-[350px] ${isEditable ? 'group cursor-pointer' : 'cursor-default'}`}>
					<img
						src={coverImageUrl}
						className="w-full h-full object-cover group-hover:opacity-50 transition-opacity"
						alt="Cover background"
					/>
					<div className="absolute top-2 right-2 sm:top-4 sm:right-4 opacity-0 group-hover:opacity-100 transition-opacity z-10">
						<img src="/edit-pen.png" alt="Editar capa" className="w-12 sm:w-16" />
					</div>
				</div>
			</UploadImageModal>

			{/* Centered stripe overlay - always horizontal */}
			<div className="absolute inset-0 flex items-center justify-center pointer-events-none">
				<div className="w-full bg-[rgba(246,233,208,0.75)] py-3 sm:py-4 flex items-center justify-center gap-4 sm:gap-6 lg:gap-10">
					{/* Profile photo - bigger emphasis, responsive size */}
					<UploadImageModal id={profileId} setImageUrl={setProfileImageUrl} isEditable={isEditable} mode="profile">
						<div
							className={`relative w-32 h-32 sm:w-40 sm:h-40 lg:w-64 lg:h-64 bg-dark_purple rounded-full pointer-events-auto flex-shrink-0 ${isEditable ? 'group cursor-pointer' : 'cursor-default'}`}
						>
							{/* Gray overlay on hover */}
							<div className="absolute inset-0 bg-gray-400 opacity-0 group-hover:opacity-50 transition-opacity duration-300 rounded-full" />

							{/* Profile image */}
							<img
								src={profileImageUrl}
								alt={`${babyName}`}
								className="w-[88%] h-[88%] object-cover rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
							/>

							{/* Edit icon on hover */}
							<div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
								<img src="/edit-pen.png" alt="Editar Perfil" className="w-10 sm:w-12 lg:w-16" />
							</div>
						</div>
					</UploadImageModal>

					{/* Title section with edit functionality - horizontal layout with better mobile sizing */}
					<div
						className={`pointer-events-auto flex items-center gap-2 sm:gap-4 flex-1 lg:flex-none ${isEditable ? 'group cursor-pointer' : 'cursor-default'}`}
					>
						<BabyNameModal id={profileId} currentName={babyName || undefined} currentEventType={eventType} isEditable={isEditable}>
							<div className="flex items-center gap-2 sm:gap-4 sm:w-[950px]">
								<Title fz={{ base: '7vw', md: '4vw' }}>{`${eventType} de ${babyName || ''}`}</Title>
								<div className="opacity-0 group-hover:opacity-100 transition-opacity">
									<img src="/edit-pen.png" alt="Editar título" className="w-8 sm:w-10 lg:w-12" />
								</div>
							</div>
						</BabyNameModal>
					</div>
				</div>
			</div>
		</div>
	)
}
